// src/components/settings/SettingsPage.jsx
/**
 * @file components/settings/SettingsPage.jsx
 * @description Complete settings page for BYOK management
 * 
 * Features:
 * - View subscription tier and credits
 * - Toggle BYOK on/off
 * - Add/delete API keys for OpenAI, Anthropic, Google
 * - Test API key validity
 * 
 * Single-component design (no splitting needed for now)
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useChatSession } from '../../context/ChatSessionContext';
import { 
    saveAPIKey, 
    updateBYOKPreference, 
    deleteAPIKey, 
    testAPIKey 
} from '../../api/settings';

import ProviderIcon from './ProviderIcon';

import ConfirmationDialog from '../common/ConfirmationDialog';

import styles from '../../styles/Settings.module.css';
import {
    ArrowLeft,
    User,
    CreditCard,
    Key,
    CheckCircle,
    XCircle,
    AlertCircle,
    Info,
    Loader,
    AlertTriangle,
    Eye,
    EyeOff,
} from 'lucide-react';

/**
 * Provider configuration for API key management
 */
const PROVIDERS = [
    {
        id: 'openrouter',
        name: 'OpenRouter',
        placeholder: 'sk-or-v1-...',
        helpText: 'Get your API key from openrouter.ai/keys',
    },
    {
        id: 'google',
        name: 'Google AI',
        placeholder: 'AIza...',
        helpText: 'Get your API key from ai.google.dev',
    },
    {
        id: 'openai',
        name: 'OpenAI',
        placeholder: 'sk-...',
        helpText: 'Get your API key from platform.openai.com/api-keys',
    },
    {
        id: 'anthropic',
        name: 'Anthropic',
        placeholder: 'sk-ant-...',
        helpText: 'Get your API key from console.anthropic.com',
    },
];

/**
 * Main Settings Page Component
 */
const SettingsPage = () => {
    // --- Context Hooks ---
    const { 
        userProfile, 
        isLoadingProfile, 
        profileError,
        refreshUserProfile 
    } = useAuth();
    
    const { setViewMode } = useChatSession();
    
    // --- Local State ---
    const [activeKeyForm, setActiveKeyForm] = useState(null); // openrouter | 'openai' | 'anthropic' | 'google' | null
    const [keyInputs, setKeyInputs] = useState({
        openrouter: '',
        google: '',
        openai: '',
        anthropic: ''
    });
    const [isTogglingBYOK, setIsTogglingBYOK] = useState(false);
    const [isSavingKey, setIsSavingKey] = useState(false);
    const [isDeletingKey, setIsDeletingKey] = useState(null);
    const [isTestingKey, setIsTestingKey] = useState(null);
    const [testResults, setTestResults] = useState({});
    const [actionError, setActionError] = useState(null);
    const [actionSuccess, setActionSuccess] = useState(null);
    const [keyToDelete, setKeyToDelete] = useState(null);
    const [showKeyValues, setShowKeyValues] = useState({});
    
    console.log('SettingsPage: Rendering settings page');
    
    // --- Clear messages after 5 seconds ---
    useEffect(() => {
        if (actionError || actionSuccess) {
            const timer = setTimeout(() => {
                setActionError(null);
                setActionSuccess(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [actionError, actionSuccess]);
    
    // --- Handler: Go back to chat ---
    const handleBack = () => {
        console.log('SettingsPage: Navigating back to chat');
        setViewMode('chat');
    };
    
    // --- Handler: Toggle BYOK ---
    const handleToggleBYOK = async (newValue) => {
        console.log(`SettingsPage: Toggling BYOK to ${newValue}`);
        
        // Check if enabling BYOK without any keys
        if (newValue && !userProfile?.apiKeys?.openai?.isSet && !userProfile?.apiKeys?.anthropic?.isSet && !userProfile?.apiKeys?.google?.isSet && !userProfile?.apiKeys?.openrouter?.isSet) {
            setActionError('You must add at least one API key before enabling BYOK');
            return;
        }
        
        setIsTogglingBYOK(true);
        setActionError(null);
        setActionSuccess(null);
        
        try {
            await updateBYOKPreference(newValue);
            await refreshUserProfile();
            
            setActionSuccess(
                newValue 
                    ? 'BYOK enabled! You are now using your own API keys.'
                    : 'BYOK disabled. You are now using app credits.'
            );
            console.log('SettingsPage: BYOK toggle successful');
        } catch (error) {
            console.error('SettingsPage: Failed to toggle BYOK:', error);
            setActionError(error.message || 'Failed to update BYOK preference');
        } finally {
            setIsTogglingBYOK(false);
        }
    };
    
    // --- Handler: Save API Key ---
    const handleSaveKey = async (provider) => {
        console.log(`SettingsPage: Saving ${provider} API key`);
        
        const apiKey = keyInputs[provider].trim();
        
        if (!apiKey) {
            setActionError('Please enter an API key');
            return;
        }
        
        setIsSavingKey(true);
        setActionError(null);
        setActionSuccess(null);
        
        try {
            await saveAPIKey(provider, apiKey);
            await refreshUserProfile();
            
            // Clear input and close form
            setKeyInputs(prev => ({ ...prev, [provider]: '' }));
            setActiveKeyForm(null);
            
            setActionSuccess(`${PROVIDERS.find(p => p.id === provider).name} API key saved successfully!`);
            console.log(`SettingsPage: ${provider} key saved successfully`);
        } catch (error) {
            console.error(`SettingsPage: Failed to save ${provider} key:`, error);
            setActionError(error.message || `Failed to save ${provider} API key`);
        } finally {
            setIsSavingKey(false);
        }
    };
    
    // --- Handler: Delete API Key ---
    const handleDeleteKey = async () => {
        if (!keyToDelete) return; // Safety check
        const provider = keyToDelete;
        console.log(`SettingsPage: Deleting ${provider} API key`);
        
        setIsDeletingKey(provider);
        setActionError(null);
        setActionSuccess(null);
        
        try {
            await deleteAPIKey(provider);
            await refreshUserProfile();
            
            // Clear test result
            setTestResults(prev => {
                const updated = { ...prev };
                delete updated[provider];
                return updated;
            });
            
            setActionSuccess(`${PROVIDERS.find(p => p.id === provider).name} API key deleted`);
            console.log(`SettingsPage: ${provider} key deleted successfully`);
        } catch (error) {
            console.error(`SettingsPage: Failed to delete ${provider} key:`, error);
            setActionError(error.message || `Failed to delete ${provider} API key`);
        } finally {
            setIsDeletingKey(null);
            setKeyToDelete(null); // Close the modal on completion
        }
    };
    
    // --- Handler: Test API Key ---
    const handleTestKey = async (provider) => {
        console.log(`SettingsPage: Testing ${provider} API key`);
        
        setIsTestingKey(provider);
        setActionError(null);
        
        try {
            const result = await testAPIKey(provider);
            
            setTestResults(prev => ({
                ...prev,
                [provider]: result
            }));
            
            if (result.valid) {
                setActionSuccess(`${PROVIDERS.find(p => p.id === provider).name} API key is valid!`);
            } else {
                setActionError(result.error || `${PROVIDERS.find(p => p.id === provider).name} API key is invalid`);
            }
            
            console.log(`SettingsPage: ${provider} key test result:`, result);
        } catch (error) {
            console.error(`SettingsPage: Failed to test ${provider} key:`, error);
            setActionError(error.message || `Failed to test ${provider} API key`);
        } finally {
            setIsTestingKey(null);
        }
    };
    
    // --- Render: Loading State ---
    if (isLoadingProfile) {
        return (
            <div className={styles.settingsContainer}>
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <p>Loading settings...</p>
                </div>
            </div>
        );
    }
    
    // --- Render: Error State ---
    if (profileError && !userProfile) {
        return (
            <div className={styles.settingsContainer}>
                <div className={styles.settingsHeader}>
                    <button onClick={handleBack} className={styles.backButton}>
                        <ArrowLeft size={20} />
                        Back to Chat
                    </button>
                </div>
                
                <div className={styles.errorContainer}>
                    <p className={styles.errorMessage}>
                        Failed to load settings: {profileError}
                    </p>
                    <button 
                        onClick={refreshUserProfile}
                        className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonSmall}`}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }
    
    // --- Main Render ---
    return (
        <div className={styles.settingsContainer}>
            {/* Header */}
            <div className={styles.settingsHeader}>
                <button onClick={handleBack} className={styles.backButton}>
                    <ArrowLeft size={20} />
                    Back to Chat
                </button>
                
                <h1 className={styles.settingsTitle}>Settings</h1>
                <p className={styles.settingsSubtitle}>
                    Manage your subscription, credits, and API keys
                </p>
            </div>
            
            {/* Content */}
            <div className={styles.settingsContent}>
                
                {/* Action Messages */}
                {actionSuccess && (
                    <div className={`${styles.alertBox} ${styles.success}`}>
                        <CheckCircle size={20} />
                        <div className={styles.alertContent}>
                            <p className={styles.alertMessage}>{actionSuccess}</p>
                        </div>
                    </div>
                )}
                
                {actionError && (
                    <div className={`${styles.alertBox} ${styles.warning}`}>
                        <AlertCircle size={20} />
                        <div className={styles.alertContent}>
                            <p className={styles.alertMessage}>{actionError}</p>
                        </div>
                    </div>
                )}
                
                {/* User Profile Section */}
                <div className={styles.settingsSection}>
                    <h2 className={styles.sectionTitle}>
                        <User size={20} />
                        Account Information
                    </h2>
                    
                    <div className={styles.profileGrid}>
                        <div className={styles.profileItem}>
                            <span className={styles.profileLabel}>Subscription Tier</span>
                            <span className={`${styles.tierBadge} ${styles[userProfile?.subscriptionTier || 'free']}`}>
                                {userProfile?.subscriptionTier || 'Free'}
                            </span>
                        </div>
                        
                        <div className={styles.profileItem}>
                            <span className={styles.profileLabel}>Available Credits</span>
                            <span className={`${styles.creditsValue} ${parseFloat(userProfile?.usageCredits || 0) <= 100 ? styles.low : ''}`}>
                                {Math.round(parseFloat(userProfile?.usageCredits || 0))}
                                <span className={styles.creditsDollarValue}> (${(parseFloat(userProfile?.usageCredits || 0) / (userProfile?.creditsPerDollar || 1000)).toFixed(2)})</span>
                            </span>
                        </div>
                        
                        <div className={styles.profileItem}>
                            <span className={styles.profileLabel}>BYOK Status</span>
                            <span className={styles.profileValue}>
                                {userProfile?.useOwnKey ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>
                    </div>
                    
                    {/* Low Credits Warning */}
                    {!userProfile?.useOwnKey && parseFloat(userProfile?.usageCredits || 0) <= 100 && parseFloat(userProfile?.usageCredits || 0) > 0 && (
                        <div className={`${styles.alertBox} ${styles.warning}`}>
                            <AlertCircle size={20} />
                            <div className={styles.alertContent}>
                                <p className={styles.alertTitle}>Low Credits Warning</p>
                                <p className={styles.alertMessage}>
                                    You have only {Math.round(parseFloat(userProfile.usageCredits))} credits remaining. Consider enabling BYOK or upgrading your plan.
                                </p>
                            </div>
                        </div>
                    )}
                    
                    {/* Out of Credits Error */}
                    {!userProfile?.useOwnKey && parseFloat(userProfile?.usageCredits || 0) <= 0 && (
                        <div className={`${styles.alertBox} ${styles.warning}`}>
                            <XCircle size={20} />
                            <div className={styles.alertContent}>
                                <p className={styles.alertTitle}>Out of Credits</p>
                                <p className={styles.alertMessage}>
                                    You have no credits remaining. Enable BYOK below to continue using the service with your own API keys.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* BYOK Toggle Section */}
                <div className={styles.settingsSection}>
                    <h2 className={styles.sectionTitle}>
                        <CreditCard size={20} />
                        Bring Your Own Key (BYOK)
                    </h2>
                    
                    <p className={styles.sectionDescription}>
                        Use your own API keys instead of app credits. When enabled, you'll be charged directly by the AI providers for usage.
                    </p>
                    
                    <div className={styles.byokToggleContainer}>
                        <div className={styles.byokToggleInfo}>
                            <p className={styles.byokToggleTitle}>Use My API Keys</p>
                            <p className={styles.byokToggleDescription}>
                                {userProfile?.useOwnKey 
                                    ? 'You are currently using your own API keys'
                                    : 'You are currently using app credits'}
                            </p>
                        </div>
                        
                        <label className={styles.toggleSwitch}>
                            <input 
                                type="checkbox"
                                checked={userProfile?.useOwnKey || false}
                                onChange={(e) => handleToggleBYOK(e.target.checked)}
                                disabled={isTogglingBYOK}
                            />
                            <span className={styles.toggleSlider}></span>
                        </label>
                    </div>
                    
                    {/* Info Box */}
                    <div className={`${styles.alertBox} ${styles.info}`}>
                        <Info size={20} />
                        <div className={styles.alertContent}>
                            <p className={styles.alertMessage}>
                                {userProfile?.useOwnKey 
                                    ? 'You are being billed directly by the AI providers. Your app credits are not being used.'
                                    : 'Add at least one API key below before enabling BYOK. Your API keys are encrypted and never visible after saving.'}
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* API Keys Section */}
                <div className={styles.settingsSection}>
                    <h2 className={styles.sectionTitle}>
                        <Key size={20} />
                        API Key Management
                    </h2>
                    
                    <p className={styles.sectionDescription}>
                        Add your API keys from supported AI providers. Keys are encrypted and stored securely.
                    </p>
                    
                    <div className={styles.apiKeysGrid}>
                        {PROVIDERS.map(provider => {
                            const hasKey = userProfile?.apiKeys?.[provider.id]?.isSet || false;
                            const testResult = testResults[provider.id];
                            
                            return (
                                <div key={provider.id} className={styles.apiKeyCard}>
                                    <div className={`${styles.apiKeyIcon} ${styles[provider.id]}`}>
                                        <ProviderIcon providerId={provider.id} size={24} />
                                    </div>
                                    
                                    <div className={styles.apiKeyInfo}>
                                        <p className={styles.apiKeyProvider}>{provider.name}</p>
                                        <p className={`${styles.apiKeyStatus} ${hasKey ? styles.configured : styles.notConfigured}`}>
                                            {hasKey ? '✓ Key configured' : '○ No key configured'}
                                            {testResult && testResult.valid && ' (Valid)'}
                                            {testResult && !testResult.valid && ' (Invalid)'}
                                        </p>
                                    </div>
                                    
                                    <div className={styles.apiKeyActions}>
                                        {hasKey ? (
                                            <>
                                                <button
                                                    onClick={() => handleTestKey(provider.id)}
                                                    disabled={isTestingKey === provider.id}
                                                    className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonSmall}`}
                                                >
                                                    {isTestingKey === provider.id ? <Loader size={16} className="spinning" /> : 'Test'}
                                                </button>
                                                <button
                                                    onClick={() => setKeyToDelete(provider.id)}
                                                    disabled={isDeletingKey === provider.id}
                                                    className={`${styles.button} ${styles.buttonDanger} ${styles.buttonSmall}`}
                                                >
                                                    {isDeletingKey === provider.id ? <Loader size={16} className="spinning" /> : 'Delete'}
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={() => setActiveKeyForm(provider.id)}
                                                className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonSmall}`}
                                            >
                                                Add Key
                                            </button>
                                        )}
                                    </div>
                                    
                                    {/* Key Input Form */}
                                    {activeKeyForm === provider.id && (
                                        <div className={styles.keyForm}>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>API Key</label>
                                                <div className={styles.inputWrapper}>
                                                    <input
                                                        type={showKeyValues[provider.id] ? 'text' : 'password'}
                                                        value={keyInputs[provider.id]}
                                                        onChange={(e) => setKeyInputs(prev => ({
                                                            ...prev,
                                                            [provider.id]: e.target.value
                                                        }))}
                                                        placeholder={provider.placeholder}
                                                        className={styles.formInput}
                                                        disabled={isSavingKey}
                                                    />
                                                    <button
                                                        type="button"
                                                        className={styles.eyeButton}
                                                        onClick={() => setShowKeyValues(prev => ({ ...prev, [provider.id]: !prev[provider.id] }))}
                                                        aria-label={showKeyValues[provider.id] ? 'Hide key' : 'Show key'}
                                                        tabIndex={-1}
                                                    >
                                                        {showKeyValues[provider.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                                                    </button>
                                                </div>
                                                <p className={styles.formHelp}>{provider.helpText}</p>
                                            </div>
                                            
                                            <div className={styles.formActions}>
                                                <button
                                                    onClick={() => {
                                                        setActiveKeyForm(null);
                                                        setKeyInputs(prev => ({ ...prev, [provider.id]: '' }));
                                                    }}
                                                    className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonSmall}`}
                                                    disabled={isSavingKey}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => handleSaveKey(provider.id)}
                                                    className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonSmall}`}
                                                    disabled={isSavingKey || !keyInputs[provider.id].trim()}
                                                >
                                                    {isSavingKey ? <Loader size={16} className="spinning" /> : 'Save Key'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <ConfirmationDialog
                isOpen={!!keyToDelete}
                onClose={() => setKeyToDelete(null)}
                onConfirm={handleDeleteKey}
                title="Delete API Key"
                message={`Are you sure you want to delete the key for "<strong>${keyToDelete ? PROVIDERS.find(p => p.id === keyToDelete)?.name : ''}</strong>"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                isProcessing={!!isDeletingKey}
                error={actionError}
                variant="danger"
            />
        </div>
    );
};

export default SettingsPage;